import { MgaSalesGoalCommentKeys, MgaSalesGoalCommentListItemResponse } from '@ag-common-lib/public-api';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { LoadOptions } from 'devextreme/common/data';
import { GoalCommentRow, GoalCommentRowKeys } from './goal-comments.model';

export const MGA_SALES_GOAL_COMMENTS_PAGE_SIZE = 100;

const COMMENT_GRID_TO_API_FIELD_MAP: Record<string, string> = {
  [GoalCommentRowKeys.id]: MgaSalesGoalCommentKeys.id,
  [GoalCommentRowKeys.year]: MgaSalesGoalCommentKeys.year,
  [GoalCommentRowKeys.timestamp]: MgaSalesGoalCommentKeys.commentedAt,
  [GoalCommentRowKeys.authorName]: MgaSalesGoalCommentKeys.commentedByName,
  [GoalCommentRowKeys.authorDbId]: MgaSalesGoalCommentKeys.commentedByAgentId,
  [GoalCommentRowKeys.text]: MgaSalesGoalCommentKeys.comment,
};

export const mapMgaSalesGoalCommentToGoalCommentRow = (
  item: MgaSalesGoalCommentListItemResponse,
): GoalCommentRow => {
  const commentId = String(item.id);

  return {
    [GoalCommentRowKeys.id]: commentId,
    [GoalCommentRowKeys.year]: Number(item.year),
    [GoalCommentRowKeys.timestamp]: item.commentedAt ? new Date(item.commentedAt) : new Date(),
    [GoalCommentRowKeys.authorDbId]: item.commentedByAgentId,
    [GoalCommentRowKeys.authorName]: item.commentedByName ?? 'Unknown',
    [GoalCommentRowKeys.text]: item.comment,
    [BaseModelKeys.dbId]: commentId,
    [BaseModelKeys.createdDate]: item.createdAt ? new Date(item.createdAt) : undefined,
    [BaseModelKeys.updatedDate]: item.updatedAt ? new Date(item.updatedAt) : undefined,
    [BaseModelKeys.createdByEmail]: item.createdByEmail ?? undefined,
    [BaseModelKeys.updatedByEmail]: item.updatedByEmail ?? undefined,
    [BaseModelKeys.createdByAgentDbId]: item.createdByAgentId,
    [BaseModelKeys.updatedByAgentDbId]: item.updatedByAgentId,
  };
};

export const normalizeGoalCommentsLoadOptions = (
  loadOptions: LoadOptions | Record<string, unknown> | null | undefined,
): Record<string, unknown> => {
  if (!loadOptions || typeof loadOptions !== 'object') {
    return {
      skip: 0,
      take: MGA_SALES_GOAL_COMMENTS_PAGE_SIZE,
      requireTotalCount: true,
      sort: [{ selector: MgaSalesGoalCommentKeys.commentedAt, desc: true }],
    };
  }

  const normalizedLoadOptions = Object.entries(loadOptions as Record<string, unknown>).reduce<Record<string, unknown>>(
    (result, [key, value]) => {
      if (value === undefined) {
        return result;
      }

      result[key] = value;
      return result;
    },
    {},
  );

  normalizedLoadOptions.requireTotalCount = true;
  normalizedLoadOptions['skip'] ??= 0;
  normalizedLoadOptions['take'] ??= MGA_SALES_GOAL_COMMENTS_PAGE_SIZE;

  if (normalizedLoadOptions['sort']) {
    normalizedLoadOptions['sort'] = mapCommentSortSelectors(normalizedLoadOptions['sort']);
  }

  if (!normalizedLoadOptions['sort']) {
    normalizedLoadOptions['sort'] = [{ selector: MgaSalesGoalCommentKeys.commentedAt, desc: true }];
  }

  if (normalizedLoadOptions['filter']) {
    normalizedLoadOptions['filter'] = mapCommentFilterSelectors(normalizedLoadOptions['filter']);
  }

  return normalizedLoadOptions;
};

const mapCommentGridFieldToApiField = (selector: string): string => {
  return COMMENT_GRID_TO_API_FIELD_MAP[selector] ?? selector;
};

const mapCommentSortSelectors = (sort: unknown): unknown => {
  if (!Array.isArray(sort)) {
    return sort;
  }

  return sort.map(item => {
    if (!item || typeof item !== 'object') {
      return item;
    }

    const sortItem = item as { selector?: string; desc?: boolean };

    if (!sortItem.selector) {
      return item;
    }

    return {
      ...sortItem,
      selector: mapCommentGridFieldToApiField(sortItem.selector),
    };
  });
};

const mapCommentFilterSelectors = (filter: unknown): unknown => {
  if (!Array.isArray(filter)) {
    return filter;
  }

  if (filter.length === 3 && typeof filter[0] === 'string') {
    return [mapCommentGridFieldToApiField(filter[0]), filter[1], filter[2]];
  }

  return filter.map(item => {
    if (item === 'and' || item === 'or') {
      return item;
    }

    return mapCommentFilterSelectors(item);
  });
};
